import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

export interface SaveReportDialogProps {
  open: boolean;
  onClose: () => void;
  onSaveNew: (name: string) => void;
  onOverwrite: () => void;
  activeReportName: string | null;
}

export const SaveReportDialog: React.FC<SaveReportDialogProps> = ({
  open,
  onClose,
  onSaveNew,
  onOverwrite,
  activeReportName,
}) => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'choose' | 'new'>(activeReportName ? 'choose' : 'new');

  const handleOpen = () => {
    setName('');
    setMode(activeReportName ? 'choose' : 'new');
  };

  const handleSaveNew = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    onSaveNew(trimmed);
    onClose();
  };

  const handleOverwrite = () => {
    onOverwrite();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onEnter: handleOpen }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem' }}>
        Save Report
      </DialogTitle>
      <DialogContent>
        {mode === 'choose' && activeReportName ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              You are currently viewing <strong>{activeReportName}</strong>.
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={handleOverwrite}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                py: 1.5,
                borderColor: theme.palette.v4.border,
                color: theme.palette.text.primary,
                '&:hover': { borderColor: theme.palette.v4.accent, bgcolor: `${theme.palette.v4.accent}08` },
              }}
            >
              Overwrite &ldquo;{activeReportName}&rdquo;
            </Button>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setMode('new')}
              sx={{
                textTransform: 'none',
                justifyContent: 'flex-start',
                py: 1.5,
                borderColor: theme.palette.v4.border,
                color: theme.palette.text.primary,
                '&:hover': { borderColor: theme.palette.v4.accent, bgcolor: `${theme.palette.v4.accent}08` },
              }}
            >
              Save as new report
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Enter a name for your new report.
            </Typography>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Report name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleSaveNew(); }}
              slotProps={{ htmlInput: { 'aria-label': 'Report name' } }}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
          Cancel
        </Button>
        {mode === 'new' && (
          <Button
            variant="contained"
            onClick={handleSaveNew}
            disabled={!name.trim()}
            sx={{ textTransform: 'none', bgcolor: theme.palette.v4.accent, '&:hover': { bgcolor: theme.palette.v4.accent, opacity: 0.9 } }}
          >
            Save
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
